import { dateFormatter } from "@/lib/helpers/date-format"
import type { StoreInfo } from "@/platform/api/store-contract"

export interface DeliveryEstimate {
  earliest: string
  latest: string
  city?: string
}

// Date-only ISO strings parse as UTC midnight, which shifts a day back in any
// western timezone — anchor them to midday before formatting.
const parseDay = (iso: string) => new Date(iso.length === 10 ? `${iso}T12:00:00` : iso)

export const formatDeliveryWindow = ({ earliest, latest }: DeliveryEstimate, locale: string): string => {
  const shortDate = dateFormatter(locale, { weekday: "short", day: "numeric", month: "short" })
  return `${shortDate.format(parseDay(earliest))} – ${shortDate.format(parseDay(latest))}`.replaceAll(".", "")
}

const SHIPPING_ORIGIN_TIME_ZONE = "America/Mexico_City"

const MAX_PLAUSIBLE_DAYS = 90

const originDayOffsetBy = (days: number): string => {
  const today = dateFormatter("en-CA", { timeZone: SHIPPING_ORIGIN_TIME_ZONE, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date())
  const date = new Date(`${today}T12:00:00Z`)
  date.setUTCDate(date.getUTCDate() + days)
  return date.toISOString().slice(0, 10)
}

const plausibleDayCount = (value: number | null | undefined): number | null => (value != null && value > 0 && value <= MAX_PLAUSIBLE_DAYS ? value : null)

export const deliveryEstimateFromStore = (store: Pick<StoreInfo, "min_delivery_days" | "max_delivery_days"> | null | undefined): DeliveryEstimate | undefined => {
  const minimum = plausibleDayCount(store?.min_delivery_days)
  const maximum = plausibleDayCount(store?.max_delivery_days)
  if (!minimum || !maximum || maximum < minimum) return undefined
  return { earliest: originDayOffsetBy(minimum), latest: originDayOffsetBy(maximum) }
}
